import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    Grid,
    IconButton,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Pagination
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CircularProgress from '@mui/material/CircularProgress';
import { uploadImageFile, getImagesApi, deleteImageApi } from '../../../backendServices/ApiCalls';

const ImageUpload = () => {
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadTitle, setUploadTitle] = useState("");
    const [fetchingImages, setFetchingImages] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Upload Dialog State
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);

    // Delete Confirmation State
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const [deletingImage, setDeletingImage] = useState(false);

    const fetchImages = () => {
        setFetchingImages(true);
        getImagesApi(
            { page, limit: 12, search: searchQuery },
            (res) => {
                const fetchedData = res?.data?.data || res?.data?.images || res?.data || [];
                const fetchedTotalPages = res?.data?.pagination?.totalPages || 1;

                if (Array.isArray(fetchedData)) {
                    const mappedImages = fetchedData.map((img, index) => {
                        const url = typeof img === 'string' ? img : (img.url || img.imageUrl || img.Location);
                        const name = typeof img === 'string' ? `Image ${index + 1}` : (img.name || img.title || `Image ${index + 1}`);
                        const id = typeof img === 'string' ? Date.now() + index : (img.id || img._id || Date.now() + index);

                        return { id, url, name };
                    });
                    setImages(mappedImages);
                }
                setTotalPages(fetchedTotalPages);
                setFetchingImages(false);
            },
            (err) => {
                console.error("Error fetching images", err);
                setFetchingImages(false);
            }
        );
    };

    // Effect for handling page changes
    useEffect(() => {
        fetchImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // Effect for handling debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            // If already on page 1, trigger fetch. Otherwise resetting to 1 will safely trigger the page effect.
            if (page === 1) {
                fetchImages();
            } else {
                setPage(1);
            }
        }, 500);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery]);

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    const handleUploadSubmit = () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);

        // Upload files one by one or sequentially
        const uploadPromises = selectedFiles.map((file) => {
            return new Promise((resolve, reject) => {
                const formData = new FormData();
                formData.append("image", file);
                formData.append("title", uploadTitle.trim() || file.name);

                uploadImageFile(formData,
                    (res) => {
                        // Assuming response contains the uploaded image URL in some format
                        const uploadedUrl = res?.data?.imageUrl || res?.data?.url || res?.data?.Location || URL.createObjectURL(file);
                        resolve({
                            id: Date.now() + Math.random(),
                            url: uploadedUrl,
                            file,
                            name: uploadTitle || file.name
                        });
                    },
                    (error) => {
                        console.error('Error uploading file', error);
                        reject(error);
                    }
                );
            });
        });

        Promise.allSettled(uploadPromises).then((results) => {
            const successfulUploads = results
                .filter(result => result.status === 'fulfilled')
                .map(result => result.value);

            // Re-fetch images to incorporate accurate server pagination when successful uploads complete
            if (successfulUploads.length > 0) {
                if (page === 1) {
                    fetchImages();
                } else {
                    setPage(1);
                }
            }
            
            setUploading(false);
            setUploadTitle(""); // Clear title after upload
            setSelectedFiles([]); // Clear selected files
            setUploadDialogOpen(false); // Close dialog
        });
    };

    const handleCloseUploadDialog = () => {
        setUploadDialogOpen(false);
        setUploadTitle("");
        setSelectedFiles([]);
    };

    const handleDeleteClick = (id) => {
        setImageToDelete(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (!imageToDelete) return;

        setDeletingImage(true);
        deleteImageApi(
            imageToDelete,
            (res) => {
                // Re-fetch accurate pagination list from server on delete success
                fetchImages();
                setDeletingImage(false);
                setDeleteConfirmOpen(false);
                setImageToDelete(null);
            },
            (error) => {
                console.error("Failed to delete image", error);
                setDeletingImage(false);
                setDeleteConfirmOpen(false);
                setImageToDelete(null);
                // Optionally add a toast/snackbar error alert here
            }
        );
    };

    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setImageToDelete(null);
    };

    const handleCopyUrl = (url) => {
        navigator.clipboard.writeText(url);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h2" gutterBottom>
                Image Uploads
            </Typography>

            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <TextField
                    label="Search Images by Title..."
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ width: { xs: '100%', sm: 300 } }}
                />
                <Button
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                >
                    Upload New Image
                </Button>
            </Box>

            {/* Upload Dialog */}
            <Dialog
                open={uploadDialogOpen}
                onClose={!uploading ? handleCloseUploadDialog : undefined}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Upload Image</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <TextField
                            label="Image Title (Optional)"
                            variant="outlined"
                            fullWidth
                            value={uploadTitle}
                            onChange={(e) => setUploadTitle(e.target.value)}
                            disabled={uploading}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                disabled={uploading}
                            >
                                Select File
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleFileSelect}
                                />
                            </Button>
                            {selectedFiles.length > 0 && (
                                <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                    {selectedFiles[0].name}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseUploadDialog} color="primary" disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleUploadSubmit}
                        color="primary"
                        variant="contained"
                        disabled={uploading || selectedFiles.length === 0}
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {fetchingImages ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid container spacing={3}>
                        {images.map((image) => (
                            <Grid item xs={12} sm={6} md={4} key={image.id}>
                                <Card>
                                    <CardMedia
                                        component="img"
                                        height="200"
                                        image={image.url}
                                        alt={image.name}
                                        sx={{ objectFit: 'contain', bgcolor: '#f5f5f5' }}
                                    />
                                    <CardContent>
                                        <Typography variant="body2" color="text.secondary" noWrap title={image.name}>
                                            {image.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                                            <TextField
                                                size="small"
                                                value={image.url}
                                                InputProps={{
                                                    readOnly: true,
                                                }}
                                                fullWidth
                                            />
                                            <IconButton onClick={() => handleCopyUrl(image.url)} title="Copy URL">
                                                <ContentCopyIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </CardContent>
                                    <CardActions>
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleDeleteClick(image.id)}
                                        >
                                            Delete
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))}
                        {images.length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 4 }}>
                                    {searchQuery ? `No images found matching "${searchQuery}"` : "No images uploaded yet."}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                            <Pagination 
                                count={totalPages} 
                                page={page} 
                                onChange={(e, val) => setPage(val)} 
                                color="primary" 
                                size="large" 
                            />
                        </Box>
                    )}
                </>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={cancelDelete}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Deletion"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this image? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} color="primary" disabled={deletingImage}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        autoFocus
                        disabled={deletingImage}
                        startIcon={deletingImage ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {deletingImage ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ImageUpload;